import {
  CubeIcon,
  ServerIcon,
  Square3Stack3DIcon,
} from "@heroicons/react/24/outline";
import {
  ContainerSelected,
  LifeCycleScriptAttr,
} from "components/Environment/shared/BuildTypes";
import { FileCode } from "lucide-react";
import React, { useMemo, useState, useEffect } from "react";
import DockerCompose from "./DockerCompose";
import { KubernetesMode } from "./Kubernetes";
import { VMMode } from "./VMMode";
import { SingleContainerMode } from "./SingleContainerMode";
import { Badge } from "components/Graphs/Badge";
import { KubernetesClusterInfoTooltip } from "components/Environment/shared/KubernetesClusterInfoTooltip";
import { InstallJupyterOnHostToggle } from "./InstallJupyterOnHostToggle";
import { ConfigurationApplyButton } from "./ConfigurationApplyButton";

interface SetupOption {
  id: string;
  name: string;
  icon: React.ReactNode;
  badge?: string;
  description: string;
  features: string[];
  jupyterOption: "included" | "host" | "none";
  containerView?: "featured" | "custom";
  disabled?: boolean;
  disabledReason?: string;
}

interface FeaturedContainerVersionParams {
  pythonVersion?: string;
  cudaVersion?: string;
}

export interface BaseContainerSelectorProps {
  selectedContainer: ContainerSelected;
  onContainerChange: (container: ContainerSelected) => void;
  pythonVersion: string;
  onPythonVersionChange: (version: string) => void;
  cudaVersion: string;
  onCudaVersionChange: (version: string) => void;
  lifecycleScriptTab: "upload" | "paste";
  onLifecycleScriptTabChange: (tab: "upload" | "paste") => void;
  lifecycleScriptFile: File | null;
  onLifecycleScriptFileChange: (file: File | null) => void;
  showApplyButton?: boolean;
  onApply?: (
    container: ContainerSelected,
    featuredContainerParams?: FeaturedContainerVersionParams,
  ) => void;
  applyButtonText?: string;
  isApplyDisabled?: boolean;
  showCustomContainer?: boolean;
  saveDescription?: string;
  onValidationChange?: (hasError: boolean) => void;
  gridCols?: string;
  autoFocusScript?: boolean;
  hideSaveAndContinue?: boolean;
  selectedGpuName?: string;
  showRapidsOption?: boolean;
  singleContainerCustomOnly?: boolean;
  isArmInstance?: boolean;
  hideCredentials?: boolean;
  disableKubernetes?: boolean;
  kubernetesDisabledReason?: string;
}

const getSelectedMode = (container: ContainerSelected) => {
  if (container.vmBuild?.k8s && !container.vmBuild.k8s.isDisabled) {
    return "kubernetes";
  }
  if (container.vmBuild) {
    return "vm";
  }
  if (container.dockerCompose) {
    return "docker-compose";
  }
  if (container.customContainer || container.verbBuild) {
    return "single-container";
  }
  return "";
};

/**
 * Helper function to determine the default tab for lifecycle scripts.
 * Returns "paste" if an existing script exists (to show it to the user),
 * otherwise returns the parent's preference or "upload" for new containers.
 */
const getDefaultLifecycleScriptTab = (
  isActiveContainer: boolean,
  container: ContainerSelected,
  parentTabPreference: "upload" | "paste",
): "upload" | "paste" => {
  if (isActiveContainer) {
    // If there's a lifecycle script, default to paste to show it
    if (container.vmBuild?.lifeCycleScriptAttr?.script) {
      return "paste";
    }
    // Otherwise use the parent's tab preference
    return parentTabPreference;
  }
  // Default to paste for new containers
  return "paste";
};

const BaseContainerSelector: React.FC<BaseContainerSelectorProps> = ({
  selectedContainer,
  onContainerChange,
  pythonVersion,
  onPythonVersionChange,
  cudaVersion,
  onCudaVersionChange,
  lifecycleScriptTab,
  onLifecycleScriptTabChange,
  lifecycleScriptFile,
  onLifecycleScriptFileChange,
  showApplyButton = false,
  onApply,
  applyButtonText = "Apply",
  isApplyDisabled = false,
  saveDescription = "Click 'Apply' to save your script.",
  onValidationChange,
  autoFocusScript,
  hideSaveAndContinue = false,
  selectedGpuName,
  showRapidsOption = true,
  singleContainerCustomOnly = false,
  isArmInstance = false,
  hideCredentials = false,
  disableKubernetes = false,
  kubernetesDisabledReason = "Kubernetes is preconfigured for this environment. No additional cluster setup is required.",
}) => {
  const customContainerRequired = singleContainerCustomOnly || isArmInstance;
  const [selectedMode, setSelectedMode] = useState<string>(() => {
    const mode = getSelectedMode(selectedContainer);
    return mode === "kubernetes" && disableKubernetes ? "vm" : mode;
  });
  const getInitialView = () => {
    if (customContainerRequired) return "custom";
    return selectedContainer.customContainer ? "custom" : "featured";
  };

  const [containerView, setContainerView] = useState<"featured" | "custom">(
    getInitialView(),
  );
  const activeContainerView = customContainerRequired
    ? "custom"
    : containerView;

  // Persistent state for each mode
  const [vmModeState, setVmModeState] = useState<ContainerSelected>(
    selectedContainer.vmBuild && !selectedContainer.vmBuild.k8s
      ? selectedContainer
      : { vmBuild: { forceJupyterInstall: true } },
  );
  const [kubernetesModeState, setKubernetesModeState] =
    useState<ContainerSelected>(
      selectedContainer.vmBuild?.k8s &&
        !selectedContainer.vmBuild.k8s.isDisabled
        ? selectedContainer
        : {
            vmBuild: {
              forceJupyterInstall: false,
              k8s: {
                isDisabled: false,
                isDashboardEnabled: true,
              },
            },
          },
    );

  // Store lifecycle scripts by mode for easy switching
  const [scriptsByMode, setScriptsByMode] = useState<
    Record<string, LifeCycleScriptAttr | undefined>
  >({
    vm:
      selectedContainer.vmBuild && !selectedContainer.vmBuild.k8s
        ? selectedContainer.vmBuild.lifeCycleScriptAttr
        : undefined,
    kubernetes:
      selectedContainer.vmBuild?.k8s &&
      !selectedContainer.vmBuild.k8s.isDisabled
        ? selectedContainer.vmBuild.lifeCycleScriptAttr
        : undefined,
  });
  const [dockerComposeState, setDockerComposeState] =
    useState<ContainerSelected>(
      selectedContainer.dockerCompose
        ? selectedContainer
        : { dockerCompose: { jupyterInstall: false } },
    );
  const [dockerComposeJupyterInstall, setDockerComposeJupyterInstall] =
    useState<boolean>(selectedContainer.dockerCompose?.jupyterInstall ?? false);
  const [isDockerComposeValid, setIsDockerComposeValid] = useState(false);
  const [singleContainerState, setSingleContainerState] =
    useState<ContainerSelected>(
      selectedContainer.verbBuild || selectedContainer.customContainer
        ? selectedContainer
        : customContainerRequired
          ? { customContainer: { containerUrl: "", entryPoint: "" } }
          : { verbBuild: { containerUrl: "Default" } },
    );

  // Separate lifecycle state for VM mode
  const [vmLifecycleScriptTab, setVmLifecycleScriptTab] = useState<
    "upload" | "paste"
  >(
    getDefaultLifecycleScriptTab(
      Boolean(selectedContainer.vmBuild && !selectedContainer.vmBuild.k8s),
      selectedContainer,
      lifecycleScriptTab,
    ),
  );
  const [vmLifecycleScriptFile, setVmLifecycleScriptFile] =
    useState<File | null>(
      selectedContainer.vmBuild && !selectedContainer.vmBuild.k8s
        ? lifecycleScriptFile
        : null,
    );

  // Separate lifecycle state for Kubernetes mode
  const [kubernetesLifecycleScriptTab, setKubernetesLifecycleScriptTab] =
    useState<"upload" | "paste">(
      getDefaultLifecycleScriptTab(
        Boolean(
          selectedContainer.vmBuild?.k8s &&
          !selectedContainer.vmBuild.k8s.isDisabled,
        ),
        selectedContainer,
        lifecycleScriptTab,
      ),
    );
  const [kubernetesLifecycleScriptFile, setKubernetesLifecycleScriptFile] =
    useState<File | null>(
      selectedContainer.vmBuild?.k8s &&
        !selectedContainer.vmBuild.k8s.isDisabled
        ? lifecycleScriptFile
        : null,
    );

  // Local state for single container mode
  const [localPythonVersion, setLocalPythonVersion] =
    useState<string>(pythonVersion);
  const [localCudaVersion, setLocalCudaVersion] = useState<string>(cudaVersion);

  // Track script validation state
  const [hasScriptError, setHasScriptError] = useState<boolean>(false);
  const scriptSaveDescription = showApplyButton ? saveDescription : "";

  // Update local state when parent's selectedContainer changes
  // Only sync the initial state, don't overwrite when switching modes
  useEffect(() => {
    const mode = getSelectedMode(selectedContainer);
    setSelectedMode(mode === "kubernetes" && disableKubernetes ? "vm" : mode);
  }, [selectedContainer, disableKubernetes]);

  // Helper function to get current mode state
  const getCurrentModeState = () => {
    switch (selectedMode) {
      case "vm":
        return vmModeState;
      case "kubernetes":
        return kubernetesModeState;
      case "docker-compose":
        return dockerComposeState;
      case "single-container":
        return singleContainerState;
      default:
        return {};
    }
  };

  const isApplyButtonDisabledInternal = useMemo(() => {
    const currentState = getCurrentModeState();

    // If there's a script error in VM or Kubernetes mode, disable the apply button
    if (
      (selectedMode === "vm" || selectedMode === "kubernetes") &&
      hasScriptError
    ) {
      return true;
    }

    if (selectedMode === "single-container") {
      if (activeContainerView === "featured") {
        return !currentState.verbBuild?.containerUrl;
      } else if (activeContainerView === "custom") {
        return !currentState.customContainer?.containerUrl;
      }
    }
    if (selectedMode === "docker-compose") {
      const registriesAreValid =
        currentState.dockerCompose?.registries?.every(
          (registry) =>
            registry.url.trim().includes(".") &&
            Boolean(registry.username.trim()) &&
            Boolean(registry.password.trim()),
        ) ?? true;
      return (
        !isDockerComposeValid ||
        (!currentState.dockerCompose?.fileUrl &&
          !currentState.dockerCompose?.yamlString) ||
        !registriesAreValid
      );
    }

    return false;
  }, [
    selectedMode,
    vmModeState,
    activeContainerView,
    kubernetesModeState,
    dockerComposeState,
    singleContainerState,
    hasScriptError,
    isDockerComposeValid,
  ]);

  // Notify parent of validation state changes
  useEffect(() => {
    onValidationChange?.(isApplyButtonDisabledInternal);
  }, [isApplyButtonDisabledInternal, onValidationChange]);

  const setupOptions: SetupOption[] = [
    {
      id: "vm",
      name: "VM Mode",
      icon: <ServerIcon className="h-5 w-5" />,
      description: "Flexible VM with optional Jupyter",
      features: [
        "Comes with Python, Cuda, Docker ",
        "Optional Jupyter on host",
      ],
      jupyterOption: "host",
    },
    {
      id: "single-container",
      name: "Single Container",
      icon: <CubeIcon className="h-5 w-5" />,
      description: customContainerRequired
        ? "Provide your own container image"
        : "Featured or custom containers with Jupyter",
      features: customContainerRequired
        ? ["Use a custom container image", "Configure an optional entrypoint"]
        : [
            "Choose from featured containers",
            "Optionally deploy your own custom container",
          ],
      jupyterOption: "included",
    },
    {
      id: "docker-compose",
      name: "Docker Compose",
      icon: <FileCode className="h-5 w-5" />,
      description: "Upload or link to a docker-compose file",
      features: ["Multi-container applications", "Optional Jupyter on host"],
      jupyterOption: "host",
    },
    {
      id: "kubernetes",
      name: "Single-node Kubernetes",
      icon: <Square3Stack3DIcon className="h-5 w-5" />,
      badge: "BETA",
      description: "Single-node K8s cluster with kubectl and helm",
      features: [
        "kubectl and helm ready to use",
        "Deploy and manage containerized workloads",
      ],
      jupyterOption: "host",
      disabled: disableKubernetes,
      disabledReason: kubernetesDisabledReason,
    },
  ];

  return (
    <div className="">
      <div className="grid grid-cols-2 gap-4">
        {setupOptions.map((option: SetupOption) => (
          <div
            key={option.id + (option.containerView || "")}
            title={option.disabled ? option.disabledReason : undefined}
            className={`flex bg-manitoulinLightBlack items-start gap-3 p-4 rounded-md relative ${
              option.disabled
                ? "cursor-not-allowed border border-zinc-800 border-dashed"
                : "cursor-pointer hover:bg-primaryBg " +
                  (selectedMode === option.id &&
                  (!option.containerView ||
                    (option.containerView &&
                      containerView === option.containerView))
                    ? "border border-highlight bg-primaryBg"
                    : "border border-zinc-800 border-dashed")
            }`}
            onClick={() => {
              if (option.disabled) {
                return;
              }
              setSelectedMode(option.id);
              if (option.containerView) {
                setContainerView(option.containerView);
              }

              // Save current script before switching (works for both apply button modes)
              if (selectedContainer.vmBuild?.lifeCycleScriptAttr) {
                setScriptsByMode((prev) => ({
                  ...prev,
                  [selectedMode]:
                    selectedContainer.vmBuild?.lifeCycleScriptAttr,
                }));
              }

              // Build new state with mode-specific script
              const targetScript = scriptsByMode[option.id];
              let newModeState: ContainerSelected = {};

              switch (option.id) {
                case "vm":
                  newModeState = {
                    vmBuild: {
                      forceJupyterInstall: true,
                      lifeCycleScriptAttr: targetScript,
                    },
                  };
                  setVmModeState(newModeState);
                  if (targetScript?.script) onLifecycleScriptTabChange("paste");
                  break;

                case "kubernetes":
                  newModeState = {
                    vmBuild: {
                      forceJupyterInstall: false,
                      k8s: {
                        isDisabled: false,
                        isDashboardEnabled:
                          selectedContainer.vmBuild?.k8s?.isDashboardEnabled ??
                          true,
                      },
                      lifeCycleScriptAttr: targetScript,
                    },
                  };
                  setKubernetesModeState(newModeState);
                  if (targetScript?.script) onLifecycleScriptTabChange("paste");
                  break;

                case "docker-compose":
                  newModeState = dockerComposeState;
                  break;

                case "single-container":
                  if (
                    customContainerRequired &&
                    !singleContainerState.customContainer
                  ) {
                    newModeState = {
                      customContainer: { containerUrl: "", entryPoint: "" },
                    };
                    setSingleContainerState(newModeState);
                    setContainerView("custom");
                  } else {
                    newModeState = singleContainerState;
                  }
                  break;
              }

              // Only propagate to parent immediately when there's no apply button
              if (!showApplyButton) {
                onContainerChange(newModeState);
              }
            }}
          >
            {option.badge && (
              <div className="absolute top-2 right-2">
                <Badge variant="success">{option.badge}</Badge>
              </div>
            )}
            <div
              className={`text-highlight mt-0.5 ${
                option.disabled ? "opacity-50" : ""
              } ${
                selectedMode === option.id ? "text-highlight" : "text-gray-400"
              }`}
            >
              {option.icon}
            </div>
            <div className="flex flex-col">
              <div className={option.disabled ? "opacity-50" : ""}>
                <div className="flex items-center gap-2">
                  <div className="font-medium text-primary">{option.name}</div>
                </div>
                <div className="">
                  {option.features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 text-xs text-gray-300"
                    >
                      <div className="h-1 w-1 rounded-full bg-highlight"></div>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              {option.disabled && option.disabledReason && (
                <div className="mt-2 text-xs font-medium text-amber-600">
                  {option.disabledReason}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <hr className="my-3 dark:border dark:border-zinc-800" />

      {/* Configuration Options Based on Selected Mode */}
      <div className="">
        {selectedMode === "single-container" && (
          <SingleContainerMode
            containerView={activeContainerView}
            setContainerView={setContainerView}
            selectedContainer={singleContainerState}
            onContainerChange={(container) => {
              setSingleContainerState(container);
              if (!showApplyButton) {
                onContainerChange(container);
              }
            }}
            pythonVersion={localPythonVersion}
            onPythonVersionChange={setLocalPythonVersion}
            cudaVersion={localCudaVersion}
            onCudaVersionChange={setLocalCudaVersion}
            showApplyButton={showApplyButton}
            onApply={(containerToApply) => {
              onContainerChange(containerToApply);
              const featuredContainerParams =
                containerToApply.verbBuild?.containerUrl === "Default"
                  ? {
                      pythonVersion: localPythonVersion,
                      cudaVersion: localCudaVersion,
                    }
                  : undefined;
              if (featuredContainerParams) {
                onPythonVersionChange(localPythonVersion);
                onCudaVersionChange(localCudaVersion);
              }
              onApply?.(containerToApply, featuredContainerParams);
            }}
            applyButtonText={applyButtonText}
            isApplyDisabled={isApplyDisabled}
            isApplyButtonDisabledInternal={isApplyButtonDisabledInternal}
            selectedGpuName={selectedGpuName}
            showRapidsOption={showRapidsOption}
            customOnly={singleContainerCustomOnly}
            isArmInstance={isArmInstance}
            excludeRegistry={hideCredentials}
          />
        )}

        {/* Docker Compose Options */}
        {selectedMode === "docker-compose" && (
          <div className="space-y-3">
            <div className="flex flex-row items-center justify-between">
              <div className="flex flex-row items-center mb-4 text-white">
                <FileCode className="h-5 w-5 mr-2" />
                <h4 className="text-xl text-white">Docker Compose</h4>
              </div>
              {showApplyButton && (
                <ConfigurationApplyButton
                  disabled={isApplyDisabled || isApplyButtonDisabledInternal}
                  onClick={() => {
                    const containerToApply: ContainerSelected = {
                      ...dockerComposeState,
                      dockerCompose: {
                        ...dockerComposeState.dockerCompose,
                        jupyterInstall: dockerComposeJupyterInstall,
                      },
                    };
                    onContainerChange(containerToApply);
                    onApply?.(containerToApply);
                  }}
                >
                  {applyButtonText}
                </ConfigurationApplyButton>
              )}
            </div>
            <div className="mb-3">
              <InstallJupyterOnHostToggle
                id="jupyter-enabled-docker-compose"
                checked={dockerComposeJupyterInstall}
                onCheckedChange={(checked) => {
                  setDockerComposeJupyterInstall(checked);
                  // Do NOT update dockerComposeState here — that would change the
                  // containerSelected prop passed to <DockerCompose>, triggering its
                  // containerSelected useEffect and resetting validationResponse.
                  // jupyterInstall is merged into dockerComposeState in onSelectContainer.
                  if (!showApplyButton) {
                    onContainerChange({
                      ...dockerComposeState,
                      dockerCompose: {
                        ...dockerComposeState.dockerCompose,
                        jupyterInstall: checked,
                      },
                    });
                  }
                }}
              />
            </div>
            <DockerCompose
              containerSelected={dockerComposeState}
              hideSaveAndContinue={showApplyButton || hideSaveAndContinue}
              onSelectContainer={(container) => {
                const updatedContainer = {
                  ...container,
                  dockerCompose: {
                    ...container.dockerCompose,
                    jupyterInstall: dockerComposeJupyterInstall,
                  },
                };
                setDockerComposeState(updatedContainer);
                if (!showApplyButton) {
                  onContainerChange(updatedContainer);
                }
              }}
              onDraftChange={(container) => {
                setDockerComposeState({
                  ...container,
                  dockerCompose: {
                    ...container.dockerCompose,
                    jupyterInstall: dockerComposeJupyterInstall,
                  },
                });
              }}
              onComposeValidationChange={setIsDockerComposeValid}
              hideCredentials={hideCredentials}
              jupyterInstall={dockerComposeJupyterInstall}
              onJupyterInstallChange={setDockerComposeJupyterInstall}
              presentation="sections"
            />
          </div>
        )}

        {/* VM Mode Options */}
        {selectedMode === "vm" && (
          <div className="space-y-3">
            {showApplyButton && (
              <div className="flex flex-row items-center justify-between">
                <div className="flex flex-row items-center mb-4 text-white">
                  <ServerIcon className="h-5 w-5 mr-2" />
                  <h4 className="text-xl text-white">VM Mode</h4>
                </div>
                <ConfigurationApplyButton
                  disabled={isApplyDisabled || isApplyButtonDisabledInternal}
                  onClick={() => {
                    // Update parent state on apply
                    onContainerChange(vmModeState);
                    onLifecycleScriptTabChange(vmLifecycleScriptTab);
                    onLifecycleScriptFileChange(vmLifecycleScriptFile);
                    onApply?.(vmModeState);
                  }}
                >
                  {applyButtonText}
                </ConfigurationApplyButton>
              </div>
            )}
            <VMMode
              saveDescription={scriptSaveDescription}
              selectedContainer={vmModeState}
              setSelectedContainer={(container) => {
                setVmModeState(container);
                // Update parent immediately if no apply button
                if (!showApplyButton) {
                  onContainerChange(container);
                }
              }}
              localLifecycleScriptTab={vmLifecycleScriptTab}
              setLocalLifecycleScriptTab={setVmLifecycleScriptTab}
              localLifecycleScriptFile={vmLifecycleScriptFile}
              setLocalLifecycleScriptFile={setVmLifecycleScriptFile}
              onScriptValidationChange={(isValid) =>
                setHasScriptError(!isValid)
              }
              autoFocusScript={autoFocusScript}
            />
          </div>
        )}

        {/* Kubernetes Mode Options */}
        {selectedMode === "kubernetes" && (
          <div className="space-y-3">
            {showApplyButton && (
              <div className="flex flex-row items-center justify-between">
                <div className="flex flex-row items-center mb-4 text-white gap-2">
                  <Square3Stack3DIcon className="h-5 w-5" />
                  <h4 className="text-xl text-white">Single-node Kubernetes</h4>
                  <KubernetesClusterInfoTooltip />
                </div>
                <ConfigurationApplyButton
                  disabled={isApplyDisabled || isApplyButtonDisabledInternal}
                  onClick={() => {
                    // Update parent state on apply
                    onContainerChange(kubernetesModeState);
                    onLifecycleScriptTabChange(kubernetesLifecycleScriptTab);
                    onLifecycleScriptFileChange(kubernetesLifecycleScriptFile);
                    onApply?.(kubernetesModeState);
                  }}
                >
                  {applyButtonText}
                </ConfigurationApplyButton>
              </div>
            )}
            <KubernetesMode
              saveDescription={scriptSaveDescription}
              selectedContainer={kubernetesModeState}
              setSelectedContainer={(container) => {
                setKubernetesModeState(container);
                // Update parent immediately if no apply button
                if (!showApplyButton) {
                  onContainerChange(container);
                }
              }}
              localLifecycleScriptTab={kubernetesLifecycleScriptTab}
              setLocalLifecycleScriptTab={setKubernetesLifecycleScriptTab}
              localLifecycleScriptFile={kubernetesLifecycleScriptFile}
              setLocalLifecycleScriptFile={setKubernetesLifecycleScriptFile}
              onScriptValidationChange={(isValid) =>
                setHasScriptError(!isValid)
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default BaseContainerSelector;
